import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageSquare, X, Send } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'vedyah_chat_session';

export default function ChatWidget() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => {
    try { return localStorage.getItem(SESSION_KEY); } catch { return null; }
  });
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);
  const countRef = useRef(0);

  const TEXT = {
    en: {
      title: 'Chat with us',
      subtitle: 'Typically replies in a few minutes',
      startTitle: 'Start a conversation',
      startSub: 'Tell us who you are and we\u2019ll be right with you.',
      name: 'Your name',
      email: 'Your email',
      start: 'Start chatting',
      placeholder: 'Type your message...',
      send: 'Send',
      error: 'Could not reach us. Please check your connection and try again.',
      tooltip: 'Chat with us',
    },
    gu: {
      title: '\u0a85\u0aae\u0abe\u0ab0\u0abe \u0ab8\u0abe\u0aa5\u0ac7 \u0a9a\u0ac7\u0a9f \u0a95\u0ab0\u0acb',
      subtitle: '\u0ab8\u0abe\u0aae\u0abe\u0aa8\u0acd\u0aaf\u0aa4: \u0aa5\u0acb\u0aa1\u0ac0 \u0aae\u0abf\u0aa8\u0abf\u0a9f\u0acb\u0aae\u0abe\u0a82 \u0aaa\u0acd\u0ab0\u0aa4\u0abf\u0ab8\u0abe\u0aa6',
      startTitle: '\u0ab5\u0abe\u0aa4\u0a9a\u0ac0\u0aa4 \u0ab6\u0ab0\u0ac2 \u0a95\u0ab0\u0acb',
      startSub: '\u0a85\u0aae\u0aa8\u0ac7 \u0aa4\u0aae\u0abe\u0ab0\u0ac1\u0a82 \u0aa8\u0abe\u0aae \u0a85\u0aa8\u0ac7 \u0a88\u0aae\u0ac7\u0a87\u0ab2 \u0a86\u0aaa\u0acb.',
      name: '\u0aa4\u0aae\u0abe\u0ab0\u0ac1\u0a82 \u0aa8\u0abe\u0aae',
      email: '\u0aa4\u0aae\u0abe\u0ab0\u0acb \u0a88\u0aae\u0ac7\u0a87\u0ab2',
      start: '\u0a9a\u0ac7\u0a9f \u0ab6\u0ab0\u0ac2 \u0a95\u0ab0\u0acb',
      placeholder: '\u0ab8\u0a82\u0aa6\u0ac7\u0ab6 \u0ab2\u0a96\u0acb...',
      send: '\u0aae\u0acb\u0a95\u0ab2\u0acb',
      error: 'કનેક્ટ થઈ શક્યું નહીં. કૃપા કરીને ફરી પ્રયાસ કરો.',
      tooltip: '\u0a85\u0aae\u0abe\u0ab0\u0abe \u0ab8\u0abe\u0aa5\u0ac7 \u0a9a\u0ac7\u0a9f \u0a95\u0ab0\u0acb',
    }
  };
  const T = TEXT[lang] || TEXT.en;

  const scrollToEnd = useCallback(() => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: 'smooth' }), 40);
  }, []);

  // Session is unknown to the server (e.g. database was reset) -> start fresh instead of getting stuck
  const resetSession = useCallback(() => {
    try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
    countRef.current = 0;
    setSession(null);
    setMessages([]);
  }, []);

  const loadMessages = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const r = await axios.get(`${API}/chat/${sid}/messages`);
      setMessages(r.data);
      // only jump to the bottom when something new arrived (don't fight the user's scrolling on every poll)
      if (r.data.length !== countRef.current) {
        countRef.current = r.data.length;
        scrollToEnd();
      }
    } catch (e) {
      if (e.response?.status === 404) resetSession();
    }
  }, [scrollToEnd, resetSession]);

  useEffect(() => {
    if (session && open) loadMessages(session);
  }, [session, open, loadMessages]);

  // Poll for new agent replies while open
  useEffect(() => {
    if (!session || !open) return;
    const timer = setInterval(() => loadMessages(session), 5000);
    return () => clearInterval(timer);
  }, [session, open, loadMessages]);

  const startChat = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setBusy(true);
    setError('');
    try {
      const r = await axios.post(`${API}/chat/start`, { name: name.trim(), email: email.trim() });
      const sid = r.data.sessionId;
      setSession(sid);
      try { localStorage.setItem(SESSION_KEY, sid); } catch { /* ignore */ }
      countRef.current = 1;
      setMessages([r.data.greeting]);
      scrollToEnd();
    } catch {
      setError(T.error);
    } finally { setBusy(false); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim() || !session) return;
    const msg = text.trim();
    const tmpId = 'tmp' + Date.now();
    setText('');
    setError('');
    setBusy(true);
    // Optimistic
    setMessages((m) => [...m, { id: tmpId, sender: 'visitor', text: msg, createdAt: new Date().toISOString() }]);
    scrollToEnd();
    try {
      await axios.post(`${API}/chat/message`, { sessionId: session, text: msg });
      // Reload full history for consistency
      setTimeout(() => loadMessages(session), 300);
    } catch (err) {
      // message was not delivered: take it back out of the list and give the text back to the visitor
      setMessages((m) => m.filter((x) => x.id !== tmpId));
      setText(msg);
      if (err.response?.status === 404) resetSession(); else setError(T.error);
    } finally { setBusy(false); }
  };

  const openWidget = () => { setOpen(true); setHasNew(false); };

  return (
    <div className="fixed bottom-6 right-6 z-40">
      {open ? (
        <div className="w-[340px] md:w-[380px] h-[520px] bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden flex flex-col">
          <div className="px-4 py-3 flex items-center gap-3" style={{ background: 'var(--brand)', color: 'white' }}>
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
              <MessageSquare size={18} />
            </div>
            <div className="flex-1 leading-tight">
              <div className="font-semibold">{T.title}</div>
              <div className="text-[11px] text-white/70">{T.subtitle}</div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" className="text-white/80 hover:text-white transition-colors"><X size={18} /></button>
          </div>

          {!session ? (
            <form onSubmit={startChat} className="flex-1 flex flex-col p-5 gap-3 justify-center">
              <div>
                <div className="text-lg font-semibold text-slate-800">{T.startTitle}</div>
                <div className="text-sm text-slate-500 mb-3">{T.startSub}</div>
              </div>
              <input required value={name} onChange={(e) => setName(e.target.value)} placeholder={T.name} className="h-11 px-4 rounded-md border border-slate-200 outline-none focus:border-[color:var(--brand)] transition" />
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={T.email} className="h-11 px-4 rounded-md border border-slate-200 outline-none focus:border-[color:var(--brand)] transition" />
              {error && <div className="text-xs text-red-600">{error}</div>}
              <button disabled={busy} className="h-11 rounded-md bg-[color:var(--brand)] text-white font-medium hover:bg-[color:var(--brand-2)] transition-colors disabled:opacity-60">{busy ? '...' : T.start}</button>
            </form>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
                {messages.map((m) => {
                  const mine = m.sender === 'visitor';
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm shadow-sm ${mine ? 'bg-[color:var(--brand)] text-white rounded-br-sm' : m.sender === 'agent' ? 'bg-emerald-500 text-white rounded-bl-sm' : 'bg-white text-slate-800 rounded-bl-sm border border-slate-200'}`}>
                        {m.sender === 'agent' && <div className="text-[10px] uppercase tracking-widest opacity-80 mb-1">Vedyah Agent</div>}
                        <div className="whitespace-pre-wrap break-words">{m.text}</div>
                        <div className={`text-[10px] mt-1 ${mine || m.sender === 'agent' ? 'text-white/70' : 'text-slate-400'}`}>{new Date(m.createdAt).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {error && <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border-t border-red-100">{error}</div>}
              <form onSubmit={sendMessage} className="p-3 border-t border-slate-200 flex gap-2 bg-white">
                <input value={text} onChange={(e) => setText(e.target.value)} placeholder={T.placeholder} className="flex-1 h-11 px-4 rounded-md border border-slate-200 outline-none focus:border-[color:var(--brand)] transition text-sm" />
                <button disabled={busy || !text.trim()} className="h-11 px-4 rounded-md bg-[color:var(--brand)] text-white hover:bg-[color:var(--brand-2)] transition-colors disabled:opacity-60 flex items-center gap-1">
                  <Send size={15} /> <span className="hidden sm:inline text-sm">{T.send}</span>
                </button>
              </form>
            </>
          )}
        </div>
      ) : (
        <button
          onClick={openWidget}
          aria-label={T.tooltip}
          className="relative w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-white transition-transform hover:scale-110"
          style={{ background: 'var(--brand)' }}
        >
          <span className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ background: 'var(--brand)' }} />
          <MessageSquare size={24} className="relative" />
          {hasNew && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-[10px] flex items-center justify-center">!</span>}
        </button>
      )}
    </div>
  );
}
